import api from './api';

/**
 * Interfejs reprezentujący usługę
 */
export interface Service { // Zmieniono z Material na Service
  id?: number;
  service_id: string; // Zmieniono z material_id na service_id
  project_id: string;
  date: string;
  symbol: string;
  location: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  value: number;
  email?: string;
  contact?: string;
  client?: string;
  comments?: string;
  is_selected?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Serwis do obsługi operacji na usługach
 */
const serviceService = { // Zmieniono z materialService na serviceService
  /**
   * Pobiera wszystkie usługi
   * @returns {Promise<Service[]>} - Lista usług
   */
  getAllServices: async (): Promise<Service[]> => { // Zmieniono z getAllMaterials na getAllServices
    try {
      const response = await api.get('/services'); // Zmieniono z /materials na /services
      return response.data.map((service: any) => ({ // Zmieniono z material na service
        ...service,
        quantity: parseFloat(service.quantity),
        unit_price: parseFloat(service.unit_price),
        value: parseFloat(service.value),
      }));
    } catch (error) {
      console.error('Błąd podczas pobierania usług:', error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Pobiera usługi dla konkretnego projektu
   * @param {string} projectId - ID projektu
   * @returns {Promise<Service[]>} - Lista usług dla projektu
   */
  getServicesByProject: async (projectId: string): Promise<Service[]> => { // Zmieniono z getMaterialsByProject na getServicesByProject
    try {
      const response = await api.get(`/services/project/${projectId}`); // Zmieniono z /materials na /services
      return response.data.map((service: any) => ({ // Zmieniono z material na service
        ...service,
        quantity: parseFloat(service.quantity),
        unit_price: parseFloat(service.unit_price),
        value: parseFloat(service.value),
      }));
    } catch (error) {
      console.error(`Błąd podczas pobierania usług dla projektu ${projectId}:`, error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Pobiera usługę po ID
   * @param {number} id - ID usługi
   * @returns {Promise<Service>} - Usługa
   */
  getServiceById: async (id: number): Promise<Service> => { // Zmieniono z getMaterialById na getServiceById
    try {
      const response = await api.get(`/services/${id}`); // Zmieniono z /materials na /services
      const service = response.data; // Zmieniono z material na service
      return {
        ...service,
        quantity: parseFloat(service.quantity),
        unit_price: parseFloat(service.unit_price),
        value: parseFloat(service.value),
      };
    } catch (error) {
      console.error(`Błąd podczas pobierania usługi o ID ${id}:`, error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Tworzy nową usługę
   * @param {Service} service - Dane usługi
   * @returns {Promise<Service>} - Utworzona usługa
   */
  createService: async (service: Service): Promise<Service> => { // Zmieniono z createMaterial na createService
    try {
      const response = await api.post('/services', service); // Zmieniono z /materials na /services
      const newService = response.data; // Zmieniono z newMaterial na newService
      return {
        ...newService,
        quantity: parseFloat(newService.quantity),
        unit_price: parseFloat(newService.unit_price),
        value: parseFloat(newService.value),
      };
    } catch (error) {
      console.error('Błąd podczas tworzenia usługi:', error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Aktualizuje istniejącą usługę
   * @param {number} id - ID usługi
   * @param {Partial<Service>} service - Dane usługi do aktualizacji
   * @returns {Promise<Service>} - Zaktualizowana usługa
   */
  updateService: async (id: number, service: Partial<Service>): Promise<Service> => { // Zmieniono z updateMaterial na updateService
    try {
      const response = await api.put(`/services/${id}`, service); // Zmieniono z /materials na /services
      const updatedService = response.data; // Zmieniono z updatedMaterial na updatedService
      return {
        ...updatedService,
        quantity: parseFloat(updatedService.quantity),
        unit_price: parseFloat(updatedService.unit_price),
        value: parseFloat(updatedService.value),
      };
    } catch (error) {
      console.error(`Błąd podczas aktualizacji usługi o ID ${id}:`, error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Usuwa usługę
   * @param {number} id - ID usługi
   * @returns {Promise<void>}
   */
  deleteService: async (id: number): Promise<void> => { // Zmieniono z deleteMaterial na deleteService
    try {
      await api.delete(`/services/${id}`); // Zmieniono z /materials na /services
    } catch (error) {
      console.error(`Błąd podczas usuwania usługi o ID ${id}:`, error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Aktualizuje pole is_selected dla usługi
   * @param {number} id - ID usługi
   * @param {boolean} isSelected - Nowa wartość pola is_selected
   * @returns {Promise<Service>} - Zaktualizowana usługa
   */
  toggleServiceSelection: async (id: number, isSelected: boolean): Promise<Service> => { // Zmieniono z toggleMaterialSelection na toggleServiceSelection
    try {
      const response = await api.patch(`/services/${id}/selection`, { is_selected: isSelected }); // Zmieniono z /materials na /services
      const updatedService = response.data; // Zmieniono z updatedMaterial na updatedService
      return {
        ...updatedService,
        quantity: parseFloat(updatedService.quantity),
        unit_price: parseFloat(updatedService.unit_price),
        value: parseFloat(updatedService.value),
      };
    } catch (error) {
      console.error(`Błąd podczas aktualizacji zaznaczenia usługi o ID ${id}:`, error); // Zmieniono komunikat
      throw error;
    }
  },

  /**
   * Importuje usługi z MSSQL dla danego projektu
   * @param {string} projectId - ID projektu
   * @returns {Promise<{message: string, services: Service[]}>} - Informacja o zaimportowanych usługach
   */
  importServicesFromMssql: async (projectId: string): Promise<{message: string, services: Service[]}> => { // Zmieniono z importMaterialsFromMssql na importServicesFromMssql
    try {
      const response = await api.post(`/services/import/mssql/${projectId}`); // Zmieniono z /materials na /services
      const result = response.data;
      return {
        ...result,
        services: result.services.map((service: any) => ({ // Zmieniono z materials na services
          ...service,
          quantity: parseFloat(service.quantity),
          unit_price: parseFloat(service.unit_price),
          value: parseFloat(service.value),
        })),
      };
    } catch (error) {
      console.error(`Błąd podczas importowania usług z MSSQL dla projektu ${projectId}:`, error); // Zmieniono komunikat
      throw error;
    }
  }
};

export default serviceService;
